import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useApi } from '@/hooks/useApi';
import AdminEvents from '@/pages/admin/AdminEvents';
import AffiliateEvents from '@/pages/affiliate/AffiliateEvents';
import MemberEvents from '@/pages/member/MemberEvents';
import { useEffectiveRole } from '@/stores/authStore';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Calendar, ChevronDown, ChevronRight, Loader2, Users } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

type EventStatus = 'upcoming' | 'active' | 'completed' | 'cancelled';

type PublicEvent = {
  id: string;
  name: string;
  description: string | null;
  event_date: string;
  start_time: string | null;
  end_time: string | null;
  event_timezone: string | null;
  status: EventStatus;
  visibility: 'internal' | 'public';
  affiliate_name: string | null;
  participant_count: number;
  max_participants: number | null;
};

function dateLabel(eventDate: string): string {
  const [year, month, day] = eventDate.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return eventDate;
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function scheduleLabel(
  event: Pick<PublicEvent, 'event_date' | 'start_time' | 'end_time' | 'event_timezone'>
) {
  const date = dateLabel(event.event_date);
  return event.start_time && event.end_time
    ? `${date} · ${event.start_time}–${event.end_time} ${event.event_timezone || 'UTC'}`
    : date;
}

/**
 * Shared Events page that renders the appropriate component based on user role.
 */
export default function Events() {
  const effectiveRole = useEffectiveRole();

  if (effectiveRole === 'affiliate_owner' || effectiveRole === 'affiliate_coach') {
    return <AffiliateEvents />;
  }
  if (effectiveRole === 'member') {
    return <MemberEvents />;
  }
  if (effectiveRole === 'hq_admin') {
    return <AdminEvents />;
  }
  if (effectiveRole === 'agent') {
    return <AgentEventsView />;
  }
  return <PublicEventsView />;
}

function PublicEventsView() {
  const { api } = useApi();
  const {
    data,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['public-events'],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ status: 'all', visibility: 'public', limit: '50' });
      if (pageParam) params.set('cursor', pageParam);
      const response = await api.get<{
        events: PublicEvent[];
        pagination: { has_more: boolean; next_cursor: string | null };
      }>(`/events?${params.toString()}`);
      if (!response.success || !response.data) throw new Error(response.error);
      return response.data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.pagination.has_more ? lastPage.pagination.next_cursor || undefined : undefined,
  });

  const events = data?.pages.flatMap((page) => page.events) || [];
  const initialError = Boolean(error && !data);
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gradient-gold">Public Events</h1>
        <p className="mt-1 text-muted-foreground">
          Discover public ErgShoot competitions and events.
        </p>
      </div>
      {isLoading && <Loader2 className="mx-auto my-12 h-8 w-8 animate-spin text-primary" />}
      {initialError && (
        <Card>
          <CardContent className="py-12 text-center text-destructive">
            Failed to load public events: {(error as Error).message}
          </CardContent>
        </Card>
      )}
      {!isLoading && !initialError && events.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No public events are available.
          </CardContent>
        </Card>
      )}
      {!isLoading && !initialError && events.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {events.map((event) => (
            <Card key={event.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <CardTitle className="text-lg">{event.name}</CardTitle>
                  <Badge variant="outline">{event.status}</Badge>
                </div>
                {event.description && <CardDescription>{event.description}</CardDescription>}
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {scheduleLabel(event)}
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  {event.participant_count}
                  {event.max_participants !== null ? ` / ${event.max_participants}` : ''}{' '}
                  participants
                </div>
                <Badge variant="secondary">{event.affiliate_name || 'Global event'}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {hasNextPage && (
        <div className="flex justify-center">
          <Button onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Load more events
          </Button>
        </div>
      )}
      {isFetchNextPageError && (
        <Card>
          <CardContent className="flex items-center justify-between gap-3 py-4 text-sm text-destructive">
            <span>Failed to load more public events.</span>
            <Button variant="outline" size="sm" onClick={() => void fetchNextPage()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

type AgentEvent = PublicEvent & { affiliate_id: string };
type AgentAffiliate = { id: string; name: string };
type AgentParticipant = {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  signed_up_at: string;
};

function AgentEventsView() {
  const { api } = useApi();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [visibility, setVisibility] = useState('all');
  const [affiliateId, setAffiliateId] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [participantMap, setParticipantMap] = useState<Record<string, AgentParticipant[]>>({});
  const [participantErrors, setParticipantErrors] = useState<Record<string, string>>({});
  const [loadingParticipants, setLoadingParticipants] = useState<Record<string, boolean>>({});
  const participantRequestIdsRef = useRef<Record<string, number>>({});

  const queryString = useMemo(() => {
    const params = new URLSearchParams({ status, limit: '20' });
    if (search.trim()) params.set('search', search.trim());
    params.set('visibility', visibility);
    if (affiliateId !== 'all') params.set('affiliate_id', affiliateId);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    return params.toString();
  }, [affiliateId, dateFrom, dateTo, search, status, visibility]);

  const { data: affiliateData } = useQuery({
    queryKey: ['agent-event-affiliates'],
    queryFn: async () => {
      const affiliates: AgentAffiliate[] = [];
      let cursor: string | undefined;
      while (true) {
        const params = new URLSearchParams({ limit: '100' });
        if (cursor) params.set('cursor', cursor);
        const response = await api.get<{
          affiliates: AgentAffiliate[];
          pagination: { has_more: boolean; next_cursor: string | null };
        }>(`/agents/affiliates?${params.toString()}`);
        if (!response.success) throw new Error(response.error);
        affiliates.push(...(response.data?.affiliates || []));
        const nextCursor = response.data?.pagination?.has_more
          ? response.data.pagination.next_cursor || undefined
          : undefined;
        if (!nextCursor || nextCursor === cursor) return affiliates;
        cursor = nextCursor;
      }
    },
  });

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['agent-events', queryString],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams(queryString);
      if (pageParam) params.set('cursor', pageParam);
      const response = await api.get<{
        events: AgentEvent[];
        pagination: { has_more: boolean; next_cursor: string | null };
      }>(`/agents/events?${params.toString()}`);
      if (!response.success || !response.data) throw new Error(response.error);
      return response.data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.pagination.has_more ? lastPage.pagination.next_cursor || undefined : undefined,
  });

  const events = data?.pages.flatMap((page) => page.events) || [];
  const initialError = Boolean(error && !data);
  const affiliates = affiliateData || [];

  const toggleParticipants = async (eventId: string, force = false) => {
    if (!force && expandedEventId === eventId) {
      setExpandedEventId(null);
      return;
    }
    setExpandedEventId(eventId);
    setParticipantErrors((current) => {
      const next = { ...current };
      delete next[eventId];
      return next;
    });
    const requestId = (participantRequestIdsRef.current[eventId] || 0) + 1;
    participantRequestIdsRef.current[eventId] = requestId;
    setLoadingParticipants((current) => ({ ...current, [eventId]: true }));
    try {
      const response = await api.get<{ participants: AgentParticipant[] }>(
        `/events/${eventId}/participants`
      );
      if (participantRequestIdsRef.current[eventId] !== requestId) return;
      if (response.success && response.data) {
        const participants = response.data.participants;
        setParticipantMap((current) => ({
          ...current,
          [eventId]: participants,
        }));
      } else {
        setParticipantErrors((current) => ({
          ...current,
          [eventId]: response.error || 'Failed to load participants',
        }));
      }
    } catch {
      if (participantRequestIdsRef.current[eventId] !== requestId) return;
      setParticipantErrors((current) => ({
        ...current,
        [eventId]: 'Failed to load participants',
      }));
    } finally {
      if (participantRequestIdsRef.current[eventId] === requestId) {
        setLoadingParticipants((current) => ({ ...current, [eventId]: false }));
      }
    }
  };

  const statusColors: Record<string, string> = {
    upcoming: 'bg-blue-500/10 text-blue-500',
    active: 'bg-green-500/10 text-green-500',
    completed: 'bg-muted text-muted-foreground',
    cancelled: 'bg-destructive/10 text-destructive',
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gradient-gold">Events</h1>
        <p className="mt-1 text-muted-foreground">
          All ErgShoot events, with filters for agent review.
        </p>
      </div>

      <Card>
        <CardContent className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <Input
            aria-label="Search events"
            placeholder="Search events..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <select
            aria-label="Status filter"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="all">All statuses</option>
            <option value="upcoming">Upcoming</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            aria-label="Visibility filter"
            value={visibility}
            onChange={(event) => setVisibility(event.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="all">All visibility</option>
            <option value="internal">Internal</option>
            <option value="public">Public</option>
          </select>
          <select
            aria-label="Affiliate filter"
            value={affiliateId}
            onChange={(event) => setAffiliateId(event.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="all">All affiliates</option>
            {affiliates.map((affiliate) => (
              <option key={affiliate.id} value={affiliate.id}>
                {affiliate.name}
              </option>
            ))}
          </select>
          <Input
            aria-label="Date from filter"
            type="date"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
          />
          <Input
            aria-label="Date to filter"
            type="date"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
          />
        </CardContent>
      </Card>

      {isLoading && <Loader2 className="mx-auto my-12 h-8 w-8 animate-spin text-primary" />}
      {initialError && (
        <Card>
          <CardContent className="py-12 text-center text-destructive">
            Failed to load events: {(error as Error).message}
          </CardContent>
        </Card>
      )}
      {!isLoading && !initialError && events.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No events match these filters.
          </CardContent>
        </Card>
      )}
      {!isLoading && !initialError && events.length > 0 && (
        <div className="grid gap-4">
          {events.map((event) => {
            const expanded = expandedEventId === event.id;
            const eventParticipants = participantMap[event.id] || [];
            return (
              <Card key={event.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <CardTitle className="text-lg">{event.name}</CardTitle>
                      {event.description && <CardDescription>{event.description}</CardDescription>}
                    </div>
                    <Badge className={statusColors[event.status] || 'bg-muted'}>
                      {event.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {scheduleLabel(event)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {event.participant_count}
                      {event.max_participants !== null ? ` / ${event.max_participants}` : ''}{' '}
                      participants
                    </span>
                    <Badge variant="outline">{event.affiliate_name}</Badge>
                    <Badge variant="secondary">{event.visibility}</Badge>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void toggleParticipants(event.id)}
                  >
                    {expanded ? (
                      <ChevronDown className="mr-2 h-4 w-4" />
                    ) : (
                      <ChevronRight className="mr-2 h-4 w-4" />
                    )}
                    {expanded ? 'Hide participants' : 'View participants'}
                  </Button>
                  {expanded && (
                    <div className="rounded-md border">
                      {participantErrors[event.id] ? (
                        <div className="flex flex-wrap items-center gap-3 p-4 text-sm text-destructive">
                          <span>{participantErrors[event.id]}</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void toggleParticipants(event.id, true)}
                          >
                            Retry
                          </Button>
                        </div>
                      ) : loadingParticipants[event.id] ? (
                        <div className="p-4 text-sm text-muted-foreground">
                          Loading participants…
                        </div>
                      ) : eventParticipants.length === 0 ? (
                        <div className="p-4 text-sm text-muted-foreground">
                          No participants registered.
                        </div>
                      ) : (
                        eventParticipants.map((participant) => (
                          <div
                            key={participant.id}
                            className="grid gap-1 border-b p-3 text-sm last:border-0 sm:grid-cols-3"
                          >
                            <span className="font-medium">{participant.user_name}</span>
                            <span className="break-all text-muted-foreground">
                              {participant.user_email}
                            </span>
                            <span className="text-muted-foreground">
                              {new Date(participant.signed_up_at).toLocaleString()}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      {isFetchNextPageError && (
        <Card>
          <CardContent className="flex items-center justify-between gap-3 py-4 text-sm text-destructive">
            <span>Failed to load more events.</span>
            <Button variant="outline" size="sm" onClick={() => void fetchNextPage()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}
      {hasNextPage && (
        <div className="flex justify-center">
          <Button onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Load more events
          </Button>
        </div>
      )}
    </div>
  );
}
